import React, {useState} from 'react';
import {View, Text, Image, StyleSheet, TouchableOpacity} from 'react-native';
import {Match} from '../../types/diretta';
import {useTheme} from '../../hooks/useTheme';

interface MatchHeaderProps {
  match: Match;
}

export const MatchHeader: React.FC<MatchHeaderProps> = ({match}) => {
  const {colors} = useTheme();
  const [expanded, setExpanded] = useState(true);

  const getStatusText = () => {
    if (match.status === 'finished') return 'TERMINATA';
    if (match.status === 'live' && match.minute) return `${match.minute}'`;
    if (match.status === 'live') return 'LIVE';
    return match.time || '';
  };

  const formatItalianDate = (dateString: string, timeString?: string): string => {
    const days = ['DOMENICA', 'LUNEDÌ', 'MARTEDÌ', 'MERCOLEDÌ', 'GIOVEDÌ', 'VENERDÌ', 'SABATO'];
    const months = ['GEN', 'FEB', 'MAR', 'APR', 'MAG', 'GIU', 'LUG', 'AGO', 'SET', 'OTT', 'NOV', 'DIC'];
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const dayName = days[date.getDay()];
      const dayNum = date.getDate();
      const monthName = months[date.getMonth()];
      
      // Use provided time string if available, otherwise try to extract (though dateString is usually just YYYY-MM-DD)
      if (timeString) {
        return `${dayName} ${dayNum} ${monthName} - ${timeString}`;
      }

      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${dayName} ${dayNum} ${monthName} - ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const homeName = match.home_team.short_name || match.home_team.name;
  const awayName = match.away_team.short_name || match.away_team.name;

  // Collapsed: compact one-line view
  if (!expanded) {
    return (
      <TouchableOpacity
        style={[styles.collapsedContainer, {backgroundColor: colors.surface, borderBottomColor: colors.border}]}
        onPress={() => setExpanded(true)}
        activeOpacity={0.7}
      >
        <View style={styles.collapsedContent}>
          {match.home_team.logo && (
            <Image source={{uri: match.home_team.logo}} style={styles.collapsedLogo} resizeMode="contain" />
          )}
          <Text style={[styles.collapsedTeam, {color: colors.textPrimary}]}>{homeName}</Text>
          <Text style={[styles.collapsedScore, {color: colors.primary}]}>
            {match.score_home} - {match.score_away}
          </Text>
          <Text style={[styles.collapsedTeam, {color: colors.textPrimary}]}>{awayName}</Text>
          {match.away_team.logo && (
            <Image source={{uri: match.away_team.logo}} style={styles.collapsedLogo} resizeMode="contain" />
          )}
        </View>
        <Text style={[styles.toggleArrow, {color: colors.textSecondary}]}>{'\u25BC'}</Text>
      </TouchableOpacity>
    );
  }

  // Expanded: full view
  return (
    <TouchableOpacity
      style={[styles.container, {backgroundColor: colors.surface, borderBottomColor: colors.border}]}
      onPress={() => setExpanded(false)}
      activeOpacity={0.7}
    >
      {/* Match status/time */}
      <View style={styles.statusContainer}>
        <Text style={[
          styles.statusText,
          {color: colors.textSecondary},
          match.status === 'live' && styles.liveStatus,
        ]}>
          {getStatusText()}
        </Text>
        {match.status === 'live' && (
          <View style={styles.liveDot} />
        )}
      </View>

      {/* Teams and score */}
      <View style={styles.teamsContainer}>
        {/* Home team */}
        <View style={styles.teamContainer}>
          {match.home_team.logo && (
            <Image
              source={{uri: match.home_team.logo}}
              style={styles.teamLogo}
              resizeMode="contain"
            />
          )}
          <Text style={[styles.teamName, {color: colors.textPrimary}]} numberOfLines={2}>
            {homeName}
          </Text>
        </View>

        {/* Score */}
        <View style={styles.scoreContainer}>
          <Text style={[styles.score, {color: colors.primary}]}>
            {match.score_home} - {match.score_away}
          </Text>
        </View>

        {/* Away team */}
        <View style={styles.teamContainer}>
          {match.away_team.logo && (
            <Image
              source={{uri: match.away_team.logo}}
              style={styles.teamLogo}
              resizeMode="contain"
            />
          )}
          <Text style={[styles.teamName, {color: colors.textPrimary}]} numberOfLines={2}>
            {awayName}
          </Text>
        </View>
      </View>

      {/* Date */}
      <Text style={[styles.date, {color: colors.textSecondary}]}>{formatItalianDate(match.date, match.time)}</Text>

      <Text style={[styles.toggleArrow, {color: colors.textSecondary}]}>{'\u25B2'}</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f8f9fa',
    paddingVertical: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  collapsedContainer: {
    backgroundColor: '#f8f9fa',
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    gap: 4,
  },
  collapsedContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  collapsedLogo: {
    width: 22,
    height: 22,
  },
  collapsedTeam: {
    fontSize: 15,
    fontWeight: '700',
  },
  collapsedScore: {
    fontSize: 18,
    fontWeight: '800',
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  statusText: {
    color: '#666',
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  liveStatus: {
    color: '#4CAF50',
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50',
    marginLeft: 8,
  },
  teamsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  teamContainer: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  teamLogo: {
    width: 60,
    height: 60,
    marginBottom: 8,
  },
  teamName: {
    color: '#333',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  scoreContainer: {
    paddingHorizontal: 16,
  },
  score: {
    fontSize: 36,
    fontWeight: '700',
  },
  date: {
    color: '#888',
    fontSize: 12,
    marginTop: 12,
  },
  toggleArrow: {
    fontSize: 10,
    marginTop: 4,
  },
});
